// Library imports
import { RootState } from 'store';
import { DatePicker } from 'antd';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
// Local imports
import { TAB_ANALYTICS } from 'constants/paths';
import { getQueryAccountId, getQueryContactId } from 'utils';
import SearchField from 'modules/Analytics/Shared/SearchField';
import SelectAnt from 'modules/Settings/Forms/Components/SelectAnt';
import SelectWithIcon from 'modules/Campaigns/Shared/SelectWithIcon';
import { ANALYTICS_DEFAULT_DATES_FILTER } from '../../../Contacts/utils';
import ButtonRightIcon from '../../../../Common/Components/ButtonRightIcon';
import { analyticsDatesFilterChangeEmailEngagement, getAccountsEmailEngagement, getContactsEmailEngagement, setAccountsEmailEngagementEmailsByFilter, setContactEmailEngagementEmailsByFilter } from './actions';
// Icons imports
import Send from '../../../../../assets/img/icons/Send.svg';
import { ReactComponent as CalenderIcon } from '../../../../../assets/img/icons/Campaign/Calender.svg';
import EmailsByFilterComponent from '../../EmailsByFilterComponent';
import { getConstantCampaignEmailId } from 'modules/Analytics/utils';
import { getQueryCampaignId } from 'modules/EmailBrowser/utils';

export type DropDownIconsType = {
  rightIcon: JSX.Element;
  bgColor: string;
  borderColor: string;
  fColor: string;
  separatorColor: string;
};

interface IEmailEngagementFiltersProps {
  tab?: string;
  page?: string;
  hidden?: boolean;
  searchTerm: string;
  searchedText: string;
  emailsByOpens: string;
  emailsByClicks: string;
  status: { value: string; label: string };
  setSearchTerm: (val: string) => void;
  setEmailsByOpens: (val: string) => void;
  setEmailsByClicks: (val: string) => void;
  setStatus: (val: { value: string; label: string }) => void;
}

const EmailEngagementFilters: React.FC<IEmailEngagementFiltersProps> = ({
  page,
  status,
  hidden,
  searchTerm,
  searchedText,
  emailsByOpens,
  emailsByClicks,
  setStatus,
  setSearchTerm,
  setEmailsByOpens,
  setEmailsByClicks,
}) => {
  // Redux States
  const datesOptions = useSelector((state: RootState) => state.analyticsGraph.dates);
  const endDateCustom = useSelector((state: RootState) => state.emailsEngagement.endDate);
  const totalEmails = useSelector((state: RootState) => state.emailsEngagement.totalEmails);
  const startDateCustom = useSelector((state: RootState) => state.emailsEngagement.startDate);
  const emailsByFilter = useSelector((state: RootState) => state.emailsEngagement.emailsByFilter);
  const excludeAssetsIds = useSelector((state: RootState) => state.analyticsDashboard.excludeAssetsIds);
  const disableExportButton = useSelector((state: RootState) => state.emailsEngagement.exportButtonDisable);
  const selectedDatesObject = useSelector((state: RootState) => state.emailsEngagement.selectedDatesObject);
  const selectedEmailIds = useSelector((state: RootState) => state.accountsContentEngagement.emailIds ?? []);
  const excludeCampaignsIds = useSelector((state: RootState) => state.analyticsDashboard.excludeCampaignsIds);
  const appliedWorkflowsIds = useSelector((state: RootState) => state.analyticsDashboard.appliedWorkflowsIds);
  const selectedCampaignsIds = useSelector((state: RootState) => state.accountsContentEngagement.campaignIds ?? []);
  const AllCampaignsFilterCheck = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns);
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights } = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters);

  // Local States
  const [isFilterDisabled, setIsFilterDisabled] = useState(true);

  // Local variables
  const queryCampaignId = getQueryCampaignId();
  const isFromAccounts = page === TAB_ANALYTICS.ACCOUNTS;
  const statusOptions = [
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Soft Bounce', label: 'Soft Bounce' },
    { value: 'Hard Bounce', label: 'Hard Bounce' },
  ];

  // useEffects
  useEffect(() => {
    if (totalEmails === 0 && selectedDatesObject?.value === ANALYTICS_DEFAULT_DATES_FILTER.value) {
      if (isFilterDisabled !== true) {
        setIsFilterDisabled(true);
      }
    } else {
      if (isFilterDisabled !== false) {
        setIsFilterDisabled(false);
      }
    }
  }, [totalEmails, selectedDatesObject]);

  const setEmailsByFilterInRedux = (label: string) => {
    if (isFromAccounts) {
      setAccountsEmailEngagementEmailsByFilter(label);
    }
    else {
      setContactEmailEngagementEmailsByFilter(label);
    }
  }

  return (
    <Wrapper>
      <div className={`tabs-wrapper padding-top-md padding-bottom-md ${hidden ? 'invisible d-none' : 'd-flex'}`}>
        <ul className="d-flex justify-content-between w-100">
          <li>
            <ul>
              <li className={`custom-size`}>
                <SelectWithIcon
                  id="date"
                  emoji={<CalenderIcon style={{ height: 18, width: 18 }} />}
                  options={datesOptions}
                  heightWidth={15}
                  value={selectedDatesObject}
                  {...(analyticsDatesFilterChangeEmailEngagement
                    ? {
                      onChange: (value) => {
                        analyticsDatesFilterChangeEmailEngagement(value, isFromAccounts);
                      },
                    }
                    : {})}
                  customDatePicker={<DatePicker bordered={false} />}
                  disabled={false}
                  isAccountEmailEngagement={isFromAccounts}
                  isContactEmailEngagement={!isFromAccounts}
                />
              </li>
              <li>
                <EmailsByFilterComponent
                  searchedText={searchedText}
                  accountId={0}
                  filterLabelProp="Emails by"
                  isFromAccounts={isFromAccounts}
                  isEmailEngagement
                  emailsByFilter={emailsByFilter}
                  setEmailsByOpens={setEmailsByOpens}
                  setEmailsByClicks={setEmailsByClicks}
                  setEmailsByFilter={setEmailsByFilterInRedux}
                />
              </li>
              {/* Commenting for now until Apply button is applied */}
              {/* {!isFromAccounts ? ( */}
              {false ? (
                <li className="status-filter">
                  <SelectAnt
                    defaultValue={{ value: 'Status', label: 'Status' }}
                    options={statusOptions}
                    onChange={(value) => {
                      setStatus({ value: value, label: value });
                    }}
                    disabled={false}
                    value={status}
                  />
                </li>
              ) : null}
            </ul>
          </li>
          <li>
            <ul style={{ alignItems: 'center' }}>
              <li>
                <div className="scaled-width-35">
                  <SearchField
                    placeholder="Search emails..."
                    handleSearch={(value: string) => {
                      setSearchTerm(value.trim());
                    }}
                    reset={searchTerm}
                  ></SearchField>
                </div>
              </li>
              <li className={`right export`}>
                <ButtonRightIcon
                  label="Export"
                  onClick={() => {
                    // to update emails ids based on filters we need to clone the arrays
                    let selected_emails_ids = [...selectedEmailIds];
                    let selected_campaings_ids = [...selectedCampaignsIds];
                    let selected_workflows_ids = [...appliedWorkflowsIds];

                    // check if is share profile || daily IQ || weekly accounts insights
                    if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
                      let campaign_email_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
                      selected_campaings_ids = [campaign_email_id.toString()];
                      selected_emails_ids = [campaign_email_id.toString()];
                    }
                    let params = {
                      export: true,
                      end_date: endDateCustom,
                      start_date: startDateCustom,
                      ...(isFromAccounts && { emailIds: selected_emails_ids, workflowIds: selected_workflows_ids, allCampaigns: AllCampaignsFilterCheck }),
                      ...(isFromAccounts && { campaignIds: queryCampaignId ? [queryCampaignId] : selected_campaings_ids }), // selected campaigns ids
                      ...(isFromAccounts && AllCampaignsFilterCheck && { excludeCampaignsIds: excludeCampaignsIds, excludeAssetsIds: excludeAssetsIds }),
                      ...(searchTerm && { searchTerm }),
                      ...(!isFromAccounts && status.value !== 'Status' && { status: status.value }),
                      ...(isFromAccounts ? { account_id: getQueryAccountId() } : { contact_id: getQueryContactId() }),
                      ...(emailsByOpens && isFromAccounts ? { email_by_unique_opens: emailsByOpens } : { email_by_total_opens: emailsByOpens }),
                      ...(emailsByClicks && isFromAccounts ? { email_by_unique_clicks: emailsByClicks } : { email_by_total_clicks: emailsByClicks }),
                    };
                    if (isFromAccounts) {
                      getAccountsEmailEngagement(params);
                    } else {
                      getContactsEmailEngagement(params);
                    }
                  }}
                  icon={Send}
                  outlineClass="auto-width"
                  disable={disableExportButton}
                />
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </Wrapper>
  );
};

export default React.memo(EmailEngagementFilters);

const Wrapper = styled.div`
  .tabs-wrapper ul li.custom-size div[class$='-control'] {
    .css-1okebmr-indicatorSeparator {
      width: 0.1px;
    }
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: 38px !important;
      min-height: 38px !important;
    }
    @media all and (max-width: 1280px) {
      height: 30px !important;
      min-height: 30px !important;
    }
  }
  .tabs-wrapper .dropdown-selecter {
    height: 38px !important;
    min-height: 38px !important;
    width: 166px;
    border: 1px solid hsl(0, 0%, 80%);
    &:hover {
      border-color: hsl(0, 0%, 70%);
    }
    .seprator {
      margin-top: 2px;
      margin-bottom: 2px;
      background-color: #dddddd;
    }
    .seprator + .p-2 {
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        padding: 0.5rem !important;
        display: flex;
      }
    }
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: 38px !important;
      min-height: 38px !important;
    }
    @media all and (max-width: 1280px) {
      height: 30px !important;
      min-height: 30px !important;
    }
    .label {
      font-family: 'Roboto';
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 13px !important;
        margin-top: 1px;
      }
    }
  }
  .status-filter {
    max-width: 100%;
    min-width: 120px;
    .ant-select .ant-select-selector {
      height: 38px;
      @media all and (max-width: 1280px) {
        height: 30px !important;
      }
    }
  }
  .apply-filters > button {
    min-height: 38px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      min-height: 38px;
    }
    @media all and (max-width: 1280px) {
      height: 30px !important;
      min-height: 30px !important;
    }
  }
  .css-14wyyk6-control {
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      min-height: 38px !important;
      height: 38px !important;
    }
    @media all and (max-width: 1280px) {
      height: 30px !important;
      min-height: 30px !important;
    }
  }
  .search-wraper .search-bar {
    width: 198px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: 34px !important;
    }
    @media all and (max-width: 1280px) {
      height: 30px !important;
    }
    .search-input {
      width: 100%;
    }
  }
  .css-vot459-control {
    height: 38px;
    min-height: 38px;
    min-width: 166px;
    @media all and (max-width: 1280px) {
      height: 30px;
      min-height: 30px;
    }
  }
  .export.right > button {
    min-height: 38px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      min-height: 38px;
    }
    @media all and (max-width: 1280px) {
      height: 30px !important;
      min-height: 30px !important;
    }
  }
`;
